// Helpers for community servers (BLOBL Nodes). Everything a node sends is untrusted: nodes are run by
// third parties. Data is checked and cleaned here before the UI uses it, and only ever rendered as text.

export const PROTOCOL = 1;

const str = (v, max) => (typeof v === 'string' ? v.slice(0, max) : '');
const int = (v) => (Number.isSafeInteger(Number(v)) && Number(v) > 0 ? Number(v) : null);

// Avatars are BLOBL uploads (the Core put them into the node token). Anything else is ignored,
// so a node can't make the app load images from elsewhere.
const AVATAR_RE = /^\/uploads\/[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/;
export const safeAvatar = (v) => (typeof v === 'string' && AVATAR_RE.test(v) && !v.includes('..') ? v : null);

// Server icons are served by the node itself, in one fixed format
const ICON_RE = /^\/api\/server\/icon\/[0-9a-f]{32}\.webp$/;
export const iconSrc = (node, icon) => (node?.url && typeof icon === 'string' && ICON_RE.test(icon) ? node.url + icon : null);

export function cleanUser(u) {
  const id = int(u?.id);
  if (!id) return null;
  return {
    id,
    username: str(u.username, 50) || 'unknown',
    display_name: str(u.display_name, 100) || null,
    avatar: safeAvatar(u.avatar),
  };
}

export function cleanUsers(map) {
  const out = {};
  for (const u of Object.values(map && typeof map === 'object' ? map : {})) {
    const c = cleanUser(u);
    if (c) out[c.id] = c;
  }
  return out;
}

const ROLES = ['owner', 'admin', 'moderator', 'member'];
export const cleanRole = (r) => (ROLES.includes(r) ? r : 'member');

const dateOrNull = (v) => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null);

export function cleanMember(m) {
  const u = cleanUser(m);
  return u && { ...u, role: cleanRole(m.role), joined_at: m.joined_at, timeout_until: dateOrNull(m.timeout_until) };
}

const KINDS = ['image', 'video', 'audio', 'voice', 'file'];
const UUID_RE = /^[0-9a-f-]{36}$/;
const num = (v) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : null);

// Attachments are shown with the DM components; only known kinds, and plain files stay downloads
export function cleanAttachment(a) {
  if (!a || !UUID_RE.test(String(a.id)) || !KINDS.includes(a.kind)) return null;
  return {
    id: String(a.id),
    kind: a.kind,
    mime: str(a.mime, 100),
    name: str(a.name, 200) || 'file',
    size: num(a.size),
    width: num(a.width),
    height: num(a.height),
    duration_ms: num(a.duration_ms),
    has_thumb: !!a.has_thumb,
  };
}

export function cleanMessage(m) {
  const id = int(m?.id);
  if (!id) return null;
  return {
    id,
    channel_id: int(m.channel_id),
    author_id: int(m.author_id),
    content: str(m.content, 4000),
    reply: m.reply && int(m.reply.id)
      ? { id: int(m.reply.id), author_id: int(m.reply.author_id), content: str(m.reply.content, 200), deleted: !!m.reply.deleted }
      : null,
    mentions: Array.isArray(m.mentions) ? m.mentions.map(int).filter(Boolean).slice(0, 50) : [],
    attachments: Array.isArray(m.attachments) ? m.attachments.slice(0, 10).map(cleanAttachment).filter(Boolean) : [],
    edited_at: m.edited_at || null,
    created_at: m.created_at || new Date().toISOString(),
  };
}

export function cleanStructure(d) {
  const categories = (Array.isArray(d?.categories) ? d.categories : []).slice(0, 500)
    .map(c => int(c?.id) && { id: int(c.id), name: str(c.name, 50), position: Number(c.position) || 0 })
    .filter(Boolean);
  const channels = (Array.isArray(d?.channels) ? d.channels : []).slice(0, 1000)
    .map(c => int(c?.id) && {
      id: int(c.id),
      category_id: int(c.category_id),
      name: str(c.name, 50),
      topic: str(c.topic, 300) || null,
      position: Number(c.position) || 0,
      last_message_id: int(c.last_message_id),
    })
    .filter(Boolean);
  return { categories, channels };
}

export function cleanUnread(map) {
  const out = {};
  for (const [k, v] of Object.entries(map && typeof map === 'object' ? map : {})) {
    const id = int(k);
    if (!id) continue;
    out[id] = { last_message_id: int(v?.last_message_id), last_read_id: Number(v?.last_read_id) || 0, mentions: Math.min(Number(v?.mentions) || 0, 9999) };
  }
  return out;
}

export function cleanInfo(d) {
  return {
    id: str(d?.id, 20),
    protocol: Number(d?.protocol) || 0,
    name: str(d?.name, 100) || 'Server',
    icon: typeof d?.icon === 'string' ? d.icon : null,
    description: str(d?.description, 500) || null,
    member_count: Number(d?.member_count) || 0,
    settings: {
      members_can_invite: d?.settings?.members_can_invite !== false,
      max_channels: Number(d?.settings?.max_channels) || 50,
      max_members: Number(d?.settings?.max_members) || null,
      max_upload_mb: Number(d?.settings?.max_upload_mb) || 25,
      upload_cap_mb: Number(d?.settings?.upload_cap_mb) || 25,
    },
  };
}

// Mirrors the node's permission rules for showing or hiding buttons (the node checks again)
const RANK = { member: 0, moderator: 1, admin: 2, owner: 3 };
const MIN_ROLE = {
  create_invite: 'member', manage_invites: 'admin', manage_channels: 'admin', manage_server: 'admin',
  transfer_ownership: 'owner', delete_messages: 'moderator', timeout_members: 'moderator', kick_members: 'moderator',
  view_reports: 'moderator', ban_members: 'admin', manage_moderators: 'admin', manage_admins: 'owner',
};
export function canDo(role, action, settings = {}) {
  if (!role || !MIN_ROLE[action]) return false;
  if (action === 'create_invite' && settings.members_can_invite === false) return RANK[role] >= RANK.moderator;
  return RANK[role] >= RANK[MIN_ROLE[action]];
}

// A member can only act on members with a lower role
export const outranks = (role, targetRole) => (RANK[role] ?? -1) > (RANK[targetRole] ?? 99);

export const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', moderator: 'Moderator', member: 'Member' };

// Unread state of one channel / a whole server
export const channelUnread = (u) => !!u?.last_message_id && u.last_message_id > (u.last_read_id || 0);
export function serverUnread(unread) {
  let any = false;
  let mentions = 0;
  for (const u of Object.values(unread || {})) {
    if (channelUnread(u)) any = true;
    mentions += u.mentions || 0;
  }
  return { any, mentions };
}

// Invite links: https://blobl.de/invite/<nodeId>/<code>
const INVITE_RE = /\/invite\/([a-z0-9]{10})\/([A-Za-z0-9]{6,16})(?:[/?#]|$)/;
export function parseInvite(text) {
  const m = INVITE_RE.exec(String(text || '').trim());
  return m ? { nodeId: m[1], code: m[2] } : null;
}
export const inviteLink = (nodeId, code) => `${window.location.origin}/invite/${nodeId}/${code}`;

// @username in the composer becomes <@id> for members; edits turn it back
export function encodeMentions(text, members) {
  const byName = new Map((members || []).map(m => [m.username.toLowerCase(), m.id]));
  return text.replace(/(^|[^\w@<])@([A-Za-z0-9_.]{1,50})/g, (all, pre, name) => {
    const id = byName.get(name.toLowerCase());
    return id ? `${pre}<@${id}>` : all;
  });
}
export function decodeMentions(text, users) {
  return text.replace(/<@(\d{1,10})>/g, (all, id) => (users[id] ? `@${users[id].username}` : all));
}

// The node rejects unknown routes; these remember the last channel per server
export const lastChannelKey = (nodeId) => `last_channel:${nodeId}`;
export function lastChannel(nodeId) {
  try { return int(localStorage.getItem(lastChannelKey(nodeId))); } catch { return null; }
}
export function rememberChannel(nodeId, channelId) {
  try { localStorage.setItem(lastChannelKey(nodeId), String(channelId)); } catch {}
}

// Channels in display order: uncategorized first, then categories with their channels
export function orderedStructure(categories, channels) {
  const byPos = (a, b) => a.position - b.position || a.id - b.id;
  const cats = [...categories].sort(byPos);
  const catIds = new Set(cats.map(c => c.id));
  const loose = channels.filter(c => !c.category_id || !catIds.has(c.category_id)).sort(byPos);
  return {
    loose,
    groups: cats.map(cat => ({ category: cat, channels: channels.filter(c => c.category_id === cat.id).sort(byPos) })),
  };
}
export function firstChannelId(categories, channels) {
  const { loose, groups } = orderedStructure(categories, channels);
  return loose[0]?.id || groups.find(g => g.channels.length)?.channels[0]?.id || null;
}
